/**
 * Editable agenda export (THE-6012): a real .docx built in the browser with no dependency.
 *
 * A .docx is a ZIP of three small XML parts. The ZIP here uses "stored" entries (no compression),
 * which every reader accepts; it costs a few kB per file and saves shipping a library (JSZip/docx
 * are 100+ kB each in the bundle). Nothing is sent anywhere: no route, nothing stored.
 *
 * What goes in the file is decided BEFORE this module: the builders take a model that already holds
 * only the rows the caller may export (`ownerDocxModel` / `sharedDocxModel` in `agendaShare.ts`).
 */

export interface DocxRow {
  order: number;
  start: string | null;
  title: string;
  durationMin: number;
  /** Method card title; owner export only. */
  method: string | null;
  /** Owner export only: the row is marked "só facilitador" and the file says so. */
  facilitatorOnly: boolean;
}

export interface DocxModel {
  title: string;
  startClock: string | null;
  endClock: string | null;
  totalMin: number;
  rows: DocxRow[];
}

export interface DocxLabels {
  order: string;
  start: string;
  block: string;
  duration: string;
  method: string;
  total: string;
  min: string;
  /** Shown next to a facilitator-only row in the owner's file. */
  facilitatorOnly: string;
}

/** XML 1.0 forbids most control characters even escaped; drop them, then escape the five specials. */
export function xmlEscape(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, "").replace(/[<>&"']/g, (c) =>
    c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === "&" ? "&amp;" : c === '"' ? "&quot;" : "&apos;",
  );
}

const run = (text: string, bold = false): string =>
  `<w:r>${bold ? "<w:rPr><w:b/></w:rPr>" : ""}<w:t xml:space="preserve">${xmlEscape(text)}</w:t></w:r>`;
const para = (inner: string, style?: string): string =>
  `<w:p>${style ? `<w:pPr><w:pStyle w:val="${style}"/></w:pPr>` : ""}${inner}</w:p>`;
const cell = (text: string, bold = false): string => `<w:tc><w:tcPr><w:tcW w:w="0" w:type="auto"/></w:tcPr>${para(run(text, bold))}</w:tc>`;
const tableRow = (cells: string[], bold = false): string => `<w:tr>${cells.map((c) => cell(c, bold)).join("")}</w:tr>`;

export function documentXml(model: DocxModel, labels: DocxLabels): string {
  const withMethod = model.rows.some((r) => r.method);
  const head = [labels.order, labels.start, labels.block, labels.duration, ...(withMethod ? [labels.method] : [])];
  const rows = model.rows.map((r) =>
    tableRow(
      [
        String(r.order),
        r.start ?? "",
        r.facilitatorOnly ? `${r.title} (${labels.facilitatorOnly})` : r.title,
        `${r.durationMin} ${labels.min}`,
        ...(withMethod ? [r.method ?? ""] : []),
      ],
    ),
  );
  const span = model.startClock && model.endClock ? ` · ${model.startClock}–${model.endClock}` : "";
  const borders = ["top", "left", "bottom", "right", "insideH", "insideV"]
    .map((b) => `<w:${b} w:val="single" w:sz="4" w:space="0" w:color="999999"/>`)
    .join("");
  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>` +
    para(run(model.title, true), "Title") +
    `<w:tbl><w:tblPr><w:tblW w:w="5000" w:type="pct"/><w:tblBorders>${borders}</w:tblBorders></w:tblPr>` +
    tableRow(head, true) +
    rows.join("") +
    `</w:tbl>` +
    para(run(`${labels.total}: ${model.totalMin} ${labels.min}${span}`, true)) +
    `<w:sectPr/></w:body></w:document>`
  );
}

const CONTENT_TYPES =
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
  `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
  `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
  `<Default Extension="xml" ContentType="application/xml"/>` +
  `<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>` +
  `</Types>`;
const ROOT_RELS =
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
  `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
  `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>` +
  `</Relationships>`;

let crcTable: Uint32Array | null = null;
export function crc32(data: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (const byte of data) crc = (crcTable[(crc ^ byte) & 0xff] as number) ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/** A ZIP with stored (uncompressed) entries and a fixed timestamp, so the same input gives the same bytes. */
export function zipStored(files: ReadonlyArray<{ name: string; data: Uint8Array }>): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder();
  const DOS_TIME = 0;
  const DOS_DATE = (2026 - 1980) << 9 | 1 << 5 | 1;
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const f of files) {
    const name = enc.encode(f.name);
    const crc = crc32(f.data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true); // UTF-8 names
    local.setUint16(8, 0, true); // stored
    local.setUint16(10, DOS_TIME, true);
    local.setUint16(12, DOS_DATE, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, f.data.length, true);
    local.setUint32(22, f.data.length, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    chunks.push(new Uint8Array(local.buffer), name, f.data);

    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, 0x02014b50, true);
    cd.setUint16(4, 20, true);
    cd.setUint16(6, 20, true);
    cd.setUint16(8, 0x0800, true);
    cd.setUint16(10, 0, true);
    cd.setUint16(12, DOS_TIME, true);
    cd.setUint16(14, DOS_DATE, true);
    cd.setUint32(16, crc, true);
    cd.setUint32(20, f.data.length, true);
    cd.setUint32(24, f.data.length, true);
    cd.setUint16(28, name.length, true);
    cd.setUint32(42, offset, true);
    central.push(new Uint8Array(cd.buffer), name);
    offset += 30 + name.length + f.data.length;
  }
  const cdSize = central.reduce((n, c) => n + c.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, cdSize, true);
  end.setUint32(16, offset, true);
  const all = [...chunks, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(new ArrayBuffer(all.reduce((n, c) => n + c.length, 0)));
  let at = 0;
  for (const c of all) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

export function buildDocx(model: DocxModel, labels: DocxLabels): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder();
  return zipStored([
    { name: "[Content_Types].xml", data: enc.encode(CONTENT_TYPES) },
    { name: "_rels/.rels", data: enc.encode(ROOT_RELS) },
    { name: "word/document.xml", data: enc.encode(documentXml(model, labels)) },
  ]);
}

export const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
